'use client'

import { useEffect, useState, useCallback } from 'react'
import { useUserContext } from '@/providers/UserProvider'

/**
 * Hook to manage push notification subscriptions.
 * Handles browser permission requests, service worker subscriptions,
 * and syncing with the backend push-subscriptions collection.
 */
export function usePushSubscription() {
  const { user } = useUserContext()
  const [permission, setPermission] = useState<NotificationPermission>('default')
  const [subscription, setSubscription] = useState<PushSubscription | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Check if push notifications are supported
  const isSupported = typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window

  // Get VAPID public key from environment
  const vapidPublicKey = process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY

  /**
   * Convert VAPID public key from base64 to Uint8Array
   */
  const urlBase64ToUint8Array = useCallback((base64String: string): Uint8Array => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4)
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/')

    const rawData = window.atob(base64)
    const outputArray = new Uint8Array(rawData.length)

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i)
    }
    return outputArray
  }, [])

  /**
   * Get current service worker registration
   */
  const getServiceWorkerRegistration = useCallback(async (): Promise<ServiceWorkerRegistration | null> => {
    if (!isSupported) return null

    try {
      const registration = await navigator.serviceWorker.ready
      return registration
    } catch (err) {
      console.error('Failed to get service worker registration:', err)
      return null
    }
  }, [isSupported])

  /**
   * Check current subscription status from service worker
   */
  const checkSubscription = useCallback(async () => {
    if (!isSupported || !user) return

    try {
      const registration = await getServiceWorkerRegistration()
      if (!registration) return

      const existingSubscription = await registration.pushManager.getSubscription()
      setSubscription(existingSubscription)
    } catch (err) {
      console.error('Failed to check subscription:', err)
    }
  }, [isSupported, user, getServiceWorkerRegistration])

  /**
   * Send subscription to backend API
   */
  const sendSubscriptionToBackend = useCallback(async (sub: PushSubscription): Promise<boolean> => {
    try {
      const subJSON = sub.toJSON()

      // Extract the required fields from the subscription object
      // PushSubscription.toJSON() returns: { endpoint, keys: { p256dh, auth } }
      const payload = {
        endpoint: subJSON.endpoint,
        p256dh: subJSON.keys?.p256dh,
        auth: subJSON.keys?.auth,
        userAgent: navigator.userAgent, // Store user agent as device identifier
        channels: ['default'], // Use the default notification channel
      }

      const response = await fetch('/api/push-subscriptions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        console.error('Backend error:', errorData)
        throw new Error('Failed to save subscription to backend')
      }

      return true
    } catch (err) {
      console.error('Failed to send subscription to backend:', err)
      return false
    }
  }, [])

  /**
   * Remove subscription from backend API
   */
  const removeSubscriptionFromBackend = useCallback(async (endpoint: string): Promise<boolean> => {
    try {
      const response = await fetch('/api/push-subscriptions', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ endpoint }),
      })

      if (!response.ok) {
        throw new Error('Failed to remove subscription from backend')
      }

      return true
    } catch (err) {
      console.error('Failed to remove subscription from backend:', err)
      return false
    }
  }, [])

  /**
   * Request notification permission from browser
   */
  const requestPermission = useCallback(async (): Promise<NotificationPermission> => {
    if (!isSupported) {
      setError('Push notifications are not supported in this browser')
      return 'denied'
    }

    try {
      const result = await Notification.requestPermission()
      setPermission(result)
      return result
    } catch (err) {
      console.error('Failed to request permission:', err)
      setError('Failed to request notification permission')
      return 'denied'
    }
  }, [isSupported])

  /**
   * Subscribe to push notifications
   */
  const subscribe = useCallback(async (): Promise<boolean> => {
    if (!isSupported) {
      setError('Push notifications are not supported in this browser')
      return false
    }

    if (!user) {
      setError('You must be logged in to subscribe to push notifications')
      return false
    }

    if (!vapidPublicKey) {
      setError('Push notifications are not configured')
      console.error('VAPID public key is missing')
      return false
    }

    setIsLoading(true)
    setError(null)

    try {
      // Request permission if not already granted
      let currentPermission = permission
      if (currentPermission !== 'granted') {
        currentPermission = await requestPermission()
      }

      if (currentPermission !== 'granted') {
        setError('Notification permission was denied')
        return false
      }

      // Get service worker registration
      const registration = await getServiceWorkerRegistration()
      if (!registration) {
        setError('Service worker is not available')
        return false
      }

      // Check if already subscribed
      const existingSubscription = await registration.pushManager.getSubscription()
      if (existingSubscription) {
        setSubscription(existingSubscription)
        await sendSubscriptionToBackend(existingSubscription)
        return true
      }

      // Subscribe to push notifications
      const newSubscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
      })

      setSubscription(newSubscription)

      // Send to backend
      const success = await sendSubscriptionToBackend(newSubscription)
      if (!success) {
        setError('Failed to save subscription')
        return false
      }

      return true
    } catch (err) {
      console.error('Failed to subscribe:', err)
      setError('Failed to subscribe to push notifications')
      return false
    } finally {
      setIsLoading(false)
    }
  }, [isSupported, user, permission, vapidPublicKey, requestPermission, getServiceWorkerRegistration, sendSubscriptionToBackend, urlBase64ToUint8Array])

  /**
   * Unsubscribe from push notifications
   */
  const unsubscribe = useCallback(async (): Promise<boolean> => {
    if (!isSupported) {
      return false
    }

    if (!subscription) {
      return true // Already unsubscribed
    }

    setIsLoading(true)
    setError(null)

    try {
      // Unsubscribe from browser
      await subscription.unsubscribe()

      // Remove from backend
      await removeSubscriptionFromBackend(subscription.endpoint)

      setSubscription(null)
      return true
    } catch (err) {
      console.error('Failed to unsubscribe:', err)
      setError('Failed to unsubscribe from push notifications')
      return false
    } finally {
      setIsLoading(false)
    }
  }, [isSupported, subscription, removeSubscriptionFromBackend])

  // Check permission and subscription status on mount
  useEffect(() => {
    if (!isSupported || !user) return

    // Update permission state
    setPermission(Notification.permission)

    // Check current subscription
    checkSubscription()
  }, [isSupported, user, checkSubscription])

  return {
    permission,
    subscription,
    isSubscribed: !!subscription,
    isLoading,
    error,
    isSupported,
    requestPermission,
    subscribe,
    unsubscribe,
  }
}
